import React, { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Calendar, Download, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { updates } from '../data/mockData';
import { resolveNewsTarget } from './Updates';

const NewsDetail = () => {
  const { id } = useParams();
  const item = useMemo(() => updates.find((u) => String(u.id) === String(id)), [id]);

  if (!item) return <Navigate to="/news-updates" replace />;

  const t = resolveNewsTarget(item);
  const isExternal = t.openInNewTab || /^https?:\/\//.test(t.targetUrl) || /\.pdf($|\?)/i.test(t.targetUrl);

  // The detail page IS the fallback target. Avoid the "go here to read the
  // update" CTA pointing back to itself.
  const showPrimaryCta = t.targetUrl !== `/news-updates/${item.id}`;

  return (
    <div className="min-h-screen" style={{ background: '#0A1628' }} data-testid="news-detail-page">
      <Helmet>
        <title>{`${item.title} | SALAR Technologies LLC`}</title>
        <meta name="description" content={item.excerpt || item.body?.slice(0, 200) || item.title} />
        <meta property="og:title" content={item.title} />
        <meta property="og:description" content={item.excerpt || item.body?.slice(0, 200) || item.title} />
        {item.image && <meta property="og:image" content={`https://www.salartechnologies.com${item.image}`} />}
        <meta property="og:url" content={`https://www.salartechnologies.com/news-updates/${item.id}`} />
        <link rel="canonical" href={`https://www.salartechnologies.com/news-updates/${item.id}`} />
        <meta name="robots" content="index, follow" />
      </Helmet>

      {/* Hero */}
      <section
        className="text-white relative"
        style={{
          backgroundImage: item.image
            ? `linear-gradient(rgba(10,22,40,0.7), rgba(10,22,40,0.92)), url('${item.image}')`
            : 'linear-gradient(135deg, #0A1628 0%, #0F1A2B 100%)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          padding: '64px 0 48px',
        }}
        data-testid="news-detail-hero"
      >
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <Link
              to="/news-updates"
              className="inline-flex items-center text-cyan-300 hover:text-white text-sm font-semibold mb-6 transition-colors"
              data-testid="news-detail-back"
            >
              <ArrowLeft size={16} className="mr-1.5" />
              Back to News &amp; Updates
            </Link>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              <Badge variant="outline" className="border-cyan-400 text-cyan-200 text-[11px] tracking-wider">
                {item.category}
              </Badge>
              <span
                className="text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full border"
                style={{ background: 'rgba(96,165,250,0.12)', borderColor: 'rgba(96,165,250,0.5)', color: '#93C5FD' }}
                data-testid="news-detail-badge"
              >
                {t.badgeLabel}
              </span>
              <div className="flex items-center text-[12px] font-semibold tracking-wider ml-auto" style={{ color: '#00D4FF' }}>
                <Calendar size={14} className="mr-1.5" />
                {item.date}
              </div>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-3" data-testid="news-detail-title">
              {item.title}
            </h1>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-16" style={{ background: '#0F1A2B' }}>
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto">
            <div
              className="rounded-xl p-7 md:p-9"
              style={{ background: '#1A2744', border: '1px solid rgba(0,212,255,0.25)' }}
              data-testid="news-detail-body-card"
            >
              <p className="text-base md:text-lg text-blue-100 leading-relaxed whitespace-pre-line">
                {item.body || item.excerpt}
              </p>
            </div>

            {/* Disclaimer for compliance / demo categories */}
            {(/compliance/i.test(item.category) || /demo|prototype/i.test(item.category)) && (
              <p
                className="text-xs italic text-blue-300/85 leading-relaxed mt-5 text-center"
                data-testid="news-detail-disclaimer"
              >
                Public-safe content. Not a government system, not a customer deployment, not a
                certification, and not endorsed by any agency or third party.
              </p>
            )}

            {/* CTAs */}
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              {showPrimaryCta && (
                isExternal ? (
                  <a
                    href={t.targetUrl}
                    target={t.openInNewTab ? '_blank' : undefined}
                    rel={t.openInNewTab ? 'noopener noreferrer' : undefined}
                    data-testid="news-detail-primary-cta"
                  >
                    <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">
                      {t.ctaLabel}
                      <ExternalLink className="ml-2" size={16} />
                    </Button>
                  </a>
                ) : (
                  <Link to={t.targetUrl} data-testid="news-detail-primary-cta">
                    <Button size="lg" className="bg-cyan-600 hover:bg-cyan-700 text-white font-semibold">
                      {t.ctaLabel}
                      <ArrowRight className="ml-2" size={16} />
                    </Button>
                  </Link>
                )
              )}

              {t.downloadUrl && (
                <a
                  href={t.downloadUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="news-detail-download"
                  aria-label={`Download PDF for ${item.title}`}
                  onClick={() => {
                    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
                      window.gtag('event', 'file_download', {
                        file_name: t.downloadUrl.split('/').pop().replace(/\.pdf$/i, ''),
                        file_extension: 'pdf',
                        link_url: t.downloadUrl,
                      });
                    }
                  }}
                >
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-cyan-300 text-cyan-200 hover:bg-cyan-300 hover:text-[#0a1628]"
                  >
                    <Download className="mr-2" size={16} />
                    {t.downloadLabel || 'Download PDF'}
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default NewsDetail;
